import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useAnswerQueueStore } from "../hooks/useAnswerQueue";

// How long a pass waits for this product's queued Review answers: a hung
// request (the app's client sets no timeout) must not hold Continue for good.
const QUEUE_WAIT_MS = 15_000;

// The tail of the product's answer queue, or the wait's end, whichever comes
// first. A tail never rejects (every queued post catches its own failure).
async function queuedAnswersLanded(productId: string): Promise<void> {
  const tail = useAnswerQueueStore.getState().tails[productId];
  if (tail === undefined) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    tail,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, QUEUE_WAIT_MS);
    }),
  ]);
  clearTimeout(timer);
}

// POST /api/v1/products/{pid}/runs/trigger. Mints a fresh idempotency
// key per call (gating "is a run already in flight?" is the caller's
// responsibility — TanStack's mutation isPending plus the streaming
// state on the product page handle it). The response carries run_id +
// stream_url; caller wires those to useRunStream.
//
// 402 and 429 come back as `error` from openapi-fetch; the page renders the
// line lib/triggerCopy words for it from the mutation's error (the next-step
// bar's error line on an analyzed product; ManualTriggerErrorBanner on the
// not-started page); the 409 run_in_flight join is the caller's onError.
export async function postTrigger(productId: string) {
  const idempotencyKey = crypto.randomUUID();
  const { data, error, response } = await apiClient.POST(
    "/api/v1/products/{pid}/runs/trigger",
    {
      params: {
        path: { pid: productId },
        header: {
          "X-Requested-With": "xhr",
          "Idempotency-Key": idempotencyKey,
        },
      },
      body: {},
    },
  );
  if (error) {
    // Surface the HTTP status so callers can branch on 402 / 429
    // without re-parsing the error envelope.
    const enriched = Object.assign(new Error("trigger failed"), {
      status: response.status,
      envelope: error,
      retryAfter: response.headers.get("Retry-After"),
    });
    throw enriched;
  }
  return data!;
}

// Every attempt, whatever the server answered (a start, a 402, a 429),
// invalidates the cached ["me"] so the count Settings shows is refetched and
// is never one the server has already contradicted (0051 slice 0, FD-10).
//
// A pass waits for this product's queued Review answers to land first
// (hooks/useAnswerQueue.ts, 0074), so every answer tapped before Continue is in
// the pass it starts (a Save that replaces a saved answer leaves nothing
// waiting, so Continue is offered while it is still queued).
// It calls postTrigger once the wait ends.
export function useTriggerRun(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await queuedAnswersLanded(productId);
      return postTrigger(productId);
    },
    onSettled: () => {
      // Not returned: the mutation settles without waiting on the refetch.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
